import { http } from "./http";
import type { ResolvedReference, WorkspaceReference } from "./references.api";
import { referenceLabels } from "../components/references/reference-catalog";

export type SearchType = WorkspaceReference["entityType"];
export type SearchFilter = SearchType | "ALL";
export const searchTypes = Object.keys(referenceLabels) as SearchType[];
export const searchLabels = {
  ...referenceLabels,
  ALL: "Tout",
  CANDIDATE: "Élèves",
  INSTRUCTOR: "Moniteurs",
  SECRETARY: "Secrétaires",
  TASK: "Tâches",
  NOTE: "Notes",
  MEETING: "Réunions",
  BOARD: "Tableaux",
  LESSON: "Leçons",
  EXAM: "Examens",
  VEHICLE: "Véhicules",
  INVOICE: "Factures / avoirs",
  PAYMENT: "Paiements",
  DOCUMENT: "Documents",
  RDVPERMIS_SLOT: "Créneaux RDVPermis",
};
export type SearchItem = {
  id: string;
  label: string;
  detail: string;
  href: string;
  type: SearchType;
};
export type SearchGroup = {
  type: SearchType;
  items: SearchItem[];
  hasMore: boolean;
};

export async function searchCategory(
  search: string,
  type: SearchType,
  page: number,
  signal: AbortSignal,
): Promise<SearchGroup> {
  if (type === "CANDIDATE") {
    const { data } = await http.get<{
      items: {
        id: string;
        firstName: string;
        lastName: string;
        phoneNumber?: string;
        email?: string;
        externalRef?: string;
        licenseCategory?: string;
      }[];
      hasMore: boolean;
    }>("/v1/candidates/directory-search", {
      params: { search, page, pageSize: 10 },
      signal,
    });
    if (!Array.isArray(data.items)) throw Error("Recherche indisponible");
    return {
      type,
      hasMore: data.hasMore,
      items: data.items.map((r) => ({
        id: r.id,
        type,
        label: `${r.firstName} ${r.lastName}`,
        href: `/candidates/${r.id}`,
        detail: [
          r.licenseCategory ? `Permis ${r.licenseCategory}` : "",
          r.phoneNumber,
          r.email,
          r.externalRef ? `NEPH …${r.externalRef.slice(-4)}` : "",
        ]
          .filter(Boolean)
          .join(" · "),
      })),
    };
  }
  const { data } = await http.get<{
    items: ResolvedReference[];
    hasMore: boolean;
  }>("/v1/workspace/references/search", {
    params: { search, entityType: type, page },
    signal,
  });
  if (!Array.isArray(data.items)) throw Error("Recherche indisponible");
  return {
    type,
    hasMore: data.hasMore,
    items: data.items.flatMap((r) =>
      r.available && r.href.startsWith("/") && !r.href.startsWith("//")
        ? [
            {
              id: r.entityId,
              type,
              label: r.label,
              href: r.href,
              detail:
                r.fields?.map((f) => `${f.label} : ${f.value}`).join(" · ") ||
                searchLabels[type],
            },
          ]
        : [],
    ),
  };
}
